import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, MoreHorizontal, X } from 'lucide-react';
import MarkdownMessage from './MarkdownMessage';
import {
  NUDGE_DISMISS_LABEL,
  NUDGE_DISMISSED_LINE,
  NUDGE_GO_NOW,
  NUDGE_KEEP_SAVED,
  NUDGE_LIST_LABEL,
  NUDGE_MORE_LABEL,
  NUDGE_OPEN_NEW,
  NUDGE_REMOVE,
  NUDGE_REMOVE_ASK,
  NUDGE_SAVE,
  NUDGE_SAVED,
  NUDGE_SAVE_LABEL,
  NUDGE_UNDO_LABEL,
  UNDO_MS,
  actionForVote,
  dismissedStub,
  ideaBody,
  ideaHeadline,
  noteParts,
  openedInNote,
  plainHeadline,
  voteForAction,
} from './nudgeActionCopy';

const NudgeBatch = ({
  items = [],
  hatch,
  onVote,
  onGoWith,
  onOpenNew,
  disabled = false,
}) => {
  const [pendingId, setPendingId] = useState(null);
  const [chosen, setChosen] = useState({});
  const [undoOpen, setUndoOpen] = useState({});
  const [menuId, setMenuId] = useState(null);
  const [ask, setAsk] = useState(null);
  const [openingNew, setOpeningNew] = useState(false);
  const openingRef = useRef(false);
  const undoTimers = useRef({});
  const priorVote = useRef({});
  const menuRef = useRef(null);

  useEffect(() => () => {
    Object.values(undoTimers.current).forEach((timer) => clearTimeout(timer));
  }, []);

  useEffect(() => {
    if (!menuId) return undefined;
    const onDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuId(null);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuId(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId]);

  const closeUndo = (itemId) => {
    const timer = undoTimers.current[itemId];
    if (timer) {
      clearTimeout(timer);
      delete undoTimers.current[itemId];
    }
    setUndoOpen((current) => {
      if (!current[itemId]) return current;
      const next = { ...current };
      delete next[itemId];
      return next;
    });
  };

  const openUndo = (itemId) => {
    const existing = undoTimers.current[itemId];
    if (existing) clearTimeout(existing);
    setUndoOpen((current) => ({ ...current, [itemId]: true }));
    undoTimers.current[itemId] = setTimeout(() => {
      delete undoTimers.current[itemId];
      setUndoOpen((current) => {
        if (!current[itemId]) return current;
        const next = { ...current };
        delete next[itemId];
        return next;
      });
    }, UNDO_MS);
  };

  const displayedVote = (item) => (
    Object.prototype.hasOwnProperty.call(chosen, item.id)
      ? chosen[item.id]
      : (item.vote || null)
  );

  const handleVote = async (itemId, vote, options) => {
    if (disabled || !onVote || pendingId === itemId) return false;
    const previous = Object.prototype.hasOwnProperty.call(chosen, itemId)
      ? chosen[itemId]
      : undefined;
    setChosen((current) => ({ ...current, [itemId]: vote }));
    setPendingId(itemId);
    try {
      await onVote(itemId, vote, options);
      return true;
    } catch {
      setChosen((current) => {
        const next = { ...current };
        if (previous === undefined) delete next[itemId];
        else next[itemId] = previous;
        return next;
      });
      return false;
    } finally {
      setPendingId(null);
    }
  };

  const commitDismiss = async (item, options) => {
    priorVote.current[item.id] = displayedVote(item);
    openUndo(item.id);
    const ok = await handleVote(item.id, voteForAction('dismissed'), options);
    if (!ok) closeUndo(item.id);
  };

  const handleSave = (item) => {
    closeUndo(item.id);
    const saved = actionForVote(displayedVote(item)) === 'saved';
    if (!saved) {
      handleVote(item.id, voteForAction('saved'));
      return;
    }
    setMenuId(null);
    setAsk({ itemId: item.id, kind: 'unsave' });
  };

  const handleDismiss = (item) => {
    if (disabled || !onVote || pendingId === item.id) return;
    if (actionForVote(displayedVote(item)) === 'dismissed') return;
    if (actionForVote(displayedVote(item)) === 'saved') {
      setMenuId(null);
      setAsk({ itemId: item.id, kind: 'dismiss' });
      return;
    }
    commitDismiss(item);
  };

  const answerAsk = async (remove) => {
    const pending = ask;
    setAsk(null);
    if (!pending) return;
    const item = items.find((row) => row.id === pending.itemId);
    if (!item) return;
    // Keep it saved cancels the ask. The card stays Saved and the idea stays.
    if (!remove && pending.kind === 'unsave') return;
    const options = remove ? { removeCapture: true } : undefined;
    if (pending.kind === 'dismiss') {
      await commitDismiss(item, options);
      return;
    }
    closeUndo(item.id);
    await handleVote(item.id, null, options);
  };

  const openInNewChat = async (item) => {
    if (openingRef.current || !onOpenNew) return;
    openingRef.current = true;
    setOpeningNew(true);
    setMenuId(null);
    try {
      await onOpenNew(item);
    } finally {
      openingRef.current = false;
      setOpeningNew(false);
    }
  };

  const handleUndo = async (item) => {
    if (disabled || !onVote || pendingId === item.id) return;
    const restore = priorVote.current[item.id];
    closeUndo(item.id);
    setMenuId(null);
    const remembered = restore !== undefined
      ? restore
      : (item.capture_id ? voteForAction('saved') : null);
    const next = actionForVote(remembered) === 'saved' ? voteForAction('saved') : null;
    await handleVote(item.id, next);
  };

  return (
    <div className="nudge-batch" role="list" aria-label={NUDGE_LIST_LABEL}>
      {items.map((item) => {
        const vote = displayedVote(item);
        const action = actionForVote(vote);
        const dismissed = action === 'dismissed';
        const showUndo = dismissed && Boolean(undoOpen[item.id]);
        const headline = ideaHeadline(item.text);
        const body = ideaBody(item.text);
        const asking = ask?.itemId === item.id;
        const menuOpen = menuId === item.id;
        const opened = noteParts(openedInNote(item.opened_session_name || ''), item.opened_session_name);
        return (
          <article
            key={item.id}
            className={[
              'nudge-batch__item',
              action === 'saved' ? 'nudge-batch__item--saved' : '',
              dismissed ? 'nudge-batch__item--dismissed nudge-batch__item--collapsed' : '',
              menuOpen ? 'nudge-batch__item--menu-open' : '',
            ].filter(Boolean).join(' ')}
            role="listitem"
          >
            {showUndo ? null : (
              <div className="nudge-batch__head">
                <div className="nudge-batch__headline">
                  {dismissed ? (
                    dismissedStub(plainHeadline(item.text))
                  ) : (
                    <MarkdownMessage content={headline} />
                  )}
                </div>
                {dismissed ? null : (
                  <button
                    type="button"
                    className="nudge-batch__dismiss"
                    aria-label={NUDGE_DISMISS_LABEL}
                    disabled={disabled}
                    onClick={() => handleDismiss(item)}
                  >
                    <X className="nudge-batch__dismiss-icon" aria-hidden="true" size={16} />
                  </button>
                )}
              </div>
            )}
            {!dismissed && body ? (
              <div className="nudge-batch__text chat-message__content--markdown">
                <MarkdownMessage content={body} />
              </div>
            ) : null}
            {asking ? (
              <div
                className="nudge-batch__ask"
                role="group"
                aria-label={NUDGE_REMOVE_ASK}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') answerAsk(false);
                }}
              >
                <p className="nudge-batch__ask-text">{NUDGE_REMOVE_ASK}</p>
                <div className="nudge-batch__actions">
                  <button
                    type="button"
                    className="nudge-batch__ask-btn nudge-batch__ask-btn--keep"
                    autoFocus
                    onClick={() => answerAsk(false)}
                  >
                    {NUDGE_KEEP_SAVED}
                  </button>
                  <button
                    type="button"
                    className="nudge-batch__ask-btn"
                    onClick={() => answerAsk(true)}
                  >
                    {NUDGE_REMOVE}
                  </button>
                </div>
              </div>
            ) : (
              <div className="nudge-batch__actions">
                {showUndo ? (
                  <button
                    type="button"
                    className="nudge-batch__undo"
                    aria-label={NUDGE_UNDO_LABEL}
                    disabled={disabled}
                    onClick={() => handleUndo(item)}
                  >
                    {NUDGE_DISMISSED_LINE}
                  </button>
                ) : (
                  <>
                    {dismissed ? null : (
                      <button
                        type="button"
                        className={`nudge-batch__save${action === 'saved' ? ' nudge-batch__save--active' : ''}`}
                        aria-pressed={action === 'saved'}
                        aria-label={NUDGE_SAVE_LABEL}
                        disabled={disabled}
                        onClick={() => handleSave(item)}
                      >
                        {action === 'saved' ? (
                          <Check className="nudge-batch__save-icon" aria-hidden="true" size={16} />
                        ) : null}
                        {action === 'saved' ? NUDGE_SAVED : NUDGE_SAVE}
                      </button>
                    )}
                    <div
                      className="nudge-batch__more-wrap"
                      ref={menuOpen ? menuRef : null}
                    >
                      <button
                        type="button"
                        className="nudge-batch__more"
                        aria-label={NUDGE_MORE_LABEL}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        disabled={disabled}
                        onClick={() => setMenuId(menuOpen ? null : item.id)}
                      >
                        <MoreHorizontal aria-hidden="true" size={16} />
                      </button>
                      {menuOpen ? (
                        <div className="nudge-batch__menu" role="menu" aria-label={NUDGE_MORE_LABEL}>
                          {dismissed ? (
                            <button
                              type="button"
                              role="menuitem"
                              className="nudge-batch__menu-item"
                              onClick={() => handleUndo(item)}
                            >
                              {NUDGE_UNDO_LABEL}
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                role="menuitem"
                                className="nudge-batch__menu-item"
                                onClick={() => {
                                  setMenuId(null);
                                  onGoWith?.(item);
                                }}
                              >
                                {NUDGE_GO_NOW}
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="nudge-batch__menu-item"
                                disabled={openingNew}
                                onClick={() => openInNewChat(item)}
                              >
                                {NUDGE_OPEN_NEW}
                              </button>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            )}
            {!dismissed && opened && item.opened_session_id ? (
              <p className="nudge-batch__opened">
                {opened.lead}
                <Link to={`/chat/${item.opened_session_id}`}>{opened.name}</Link>
              </p>
            ) : null}
          </article>
        );
      })}
      {hatch ? (
        <p className="nudge-batch__hatch">{hatch}</p>
      ) : null}
    </div>
  );
};

export default NudgeBatch;
